import React, { ReactElement } from "react"
import { CheckboxV2 } from "components/Form/CheckboxV2"
import { Button, Checkbox, Input, Modal } from "semantic-ui-react"
import Select from "components/Form/Select"
import { AdminParcelEventReason, ParcelStatus } from "types/parcel.types"
import { AdminParcelEventType, ParcelAction } from "modules/Ops/Parcel/getParcelActions"
import { CheckboxWrapper } from "./ReturnRequestModal/RequestReturnModal.styled"
import { CarriersName } from "types/carrier.types"
import { ActionOutcomeDetails } from "components/ParcelEvent/ActionOutcomeDetails"
import {
  CarrierDeliveryCodeExplaining,
  CarrierDeliveryCodeInputCol,
  CarrierDeliveryCodeInputRow,
} from "./AdminEventModal.styled"
import { Col } from "reactstrap"
import { DeliveryCodeProvider } from "types/order.types"
import { AdminEventModalState, AdminEventModalStateReason } from "../ParcelHeader/parcel-header.types"

interface AdminEventModalProps {
  adminEventModal: AdminEventModalState
  submitAdminEvent: () => void
  cancelAdminEventModal: () => void
  parcelActions: ParcelAction[]
  changeReason: (reason: AdminEventModalStateReason) => void
  changeReassignStatus: (reassignStatus: ParcelStatus) => void
  changeDetails: (details: string) => void
  changeData: (key: string, value: string | object | boolean) => void
  changeIsAlreadyCancelled: (isAlreadyCancelled: boolean) => void
  isAlreadyCancelled: boolean
  returningCarrierName: CarriersName
  deliveryCodeProvider?: DeliveryCodeProvider
}

const AdminEventModal = ({
  adminEventModal,
  submitAdminEvent,
  cancelAdminEventModal,
  parcelActions,
  changeReason,
  changeReassignStatus,
  changeDetails,
  changeData,
  changeIsAlreadyCancelled,
  isAlreadyCancelled,
  returningCarrierName,
  deliveryCodeProvider,
}: AdminEventModalProps): ReactElement => {
  const { data } = adminEventModal
  const parcelAction = parcelActions.find(i => i.value === adminEventModal.type)

  const isButtonDisabled =
    !adminEventModal.type ||
    (adminEventModal.type === AdminParcelEventType.DELIVERED_TO_RECIPIENT &&
      deliveryCodeProvider === DeliveryCodeProvider.CARRIER &&
      !data.bypassCarrierEventSending &&
      !data.carrierDeliveryCode) ||
    (adminEventModal.type === AdminParcelEventType.DELIVERED_TO_RECIPIENT && !adminEventModal.reason?.value)

  /**
   * Cancel modal : reset local states
   */
  const _cancelAdminEventModal = () => {
    cancelAdminEventModal()
  }

  /**
   * Submit modal : reset local states
   */
  const _submitAdminEvent = () => {
    submitAdminEvent()
  }

  const getModalTitle = (): string => {
    switch (adminEventModal.type) {
      case AdminParcelEventType.REMOVE_FLAG:
        return "Vous allez retirer le flag sur ce colis"
      default:
        return ""
    }
  }

  const title = getModalTitle()

  return (
    <Modal open={adminEventModal.opened}>
      <Modal.Header>En êtes vous sûr ?</Modal.Header>
      <Modal.Content>
        {title}

        {adminEventModal.type === AdminParcelEventType.CANCEL_RETURN_REQUESTED &&
          returningCarrierName === CarriersName.TUT_TUT && (
            <CheckboxWrapper>
              <Checkbox
                label="La commande a déjà été annulée"
                onClick={() => changeIsAlreadyCancelled(!isAlreadyCancelled)}
                checked={isAlreadyCancelled}
              />
            </CheckboxWrapper>
          )}

        {parcelAction?.options.length > 0 && !isAlreadyCancelled && (
          <Select
            value={adminEventModal.reason}
            onChange={changeReason}
            options={parcelAction?.options}
            placeholder="Que s'est il passé ?"
          />
        )}

        {adminEventModal.type === AdminParcelEventType.DELIVERED_TO_RECIPIENT &&
          deliveryCodeProvider === DeliveryCodeProvider.CARRIER && (
            <>
              <CarrierDeliveryCodeInputRow>
                <Col xs={6}>
                  <Input
                    fluid
                    placeholder="UPS PIN Code"
                    style={{ marginTop: "1em" }}
                    required
                    onChange={e => changeData("carrierDeliveryCode", e.target.value)}
                    className="inputText"
                  />
                </Col>
                <CarrierDeliveryCodeInputCol xs={6}>
                  <CarrierDeliveryCodeExplaining>
                    Pour valider la livraison, le keeper doit fournir le code PIN reçu par le destinataire (envoyé par
                    UPS). Pickme n’a pas accès à ce code.
                  </CarrierDeliveryCodeExplaining>
                </CarrierDeliveryCodeInputCol>
              </CarrierDeliveryCodeInputRow>
              <CarrierDeliveryCodeInputRow style={{ marginTop: "1em" }}>
                <CarrierDeliveryCodeInputCol xs={6}>
                  <CheckboxV2
                    label="Valider sans code PIN"
                    sublabel="Cette action ne déclenche pas de vérification côté UPS. À utiliser uniquement si une preuve est disponible et UPS a confirmé la livraison."
                    onChange={() => changeData("bypassCarrierEventSending", !data.bypassCarrierEventSending)}
                    checked={data.bypassCarrierEventSending}
                  />
                </CarrierDeliveryCodeInputCol>
              </CarrierDeliveryCodeInputRow>
            </>
          )}

        {adminEventModal.reason?.value === AdminParcelEventReason.DELIVERED_TO_PREVIOUSLY_ASSIGNED_KEEPER && (
          <div style={{ marginTop: 20 }}>
            <Select
              value={
                !!data.newParcelStatus
                  ? {
                      value: data.newParcelStatus,
                      label: parcelAction?.secondOptions.find(i => i.value === data.newParcelStatus)?.label,
                    }
                  : null
              }
              onChange={e => changeReassignStatus(e.value)}
              options={parcelAction?.secondOptions}
              placeholder="Dans quel statut voulez vous réassigner le colis ?"
            />
          </div>
        )}

        {adminEventModal.reason?.value === AdminParcelEventReason.OTHER && (
          <Input
            fluid
            placeholder="Préciser"
            style={{ marginTop: "1em" }}
            required
            onChange={e => changeDetails(e.target.value)}
            className="inputText"
          />
        )}

        {adminEventModal.reason?.actionOutcome && (
          <ActionOutcomeDetails actionOutcome={adminEventModal.reason.actionOutcome} />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Button color="black" onClick={() => _cancelAdminEventModal()}>
          Annuler
        </Button>
        <Button
          content="Valider"
          disabled={isButtonDisabled}
          labelPosition="right"
          icon="checkmark"
          onClick={() => _submitAdminEvent()}
          positive
        />
      </Modal.Actions>
    </Modal>
  )
}

export default AdminEventModal
